/**
 * EditProposalReplySettlePrompt — "you answered the customer; is the Merlin
 * card settled?" (kanban 006f1c9e).
 *
 * Shown by the inbox / all-emails pages right after a reply or reply-all is
 * sent on an email that carries an OPEN Merlin edit proposal. Measured on
 * production 2026-10-16: in 11 of the 13 order_edit_proposal_aging red flags
 * ever raised the desk had already answered the customer on the thread, and
 * only the card was left open — so it aged into a HIGH flag and a triage
 * session, and later expired as "never applied" (AU02393: "I will keep the
 * order as is from the original version", 21 minutes after the proposal).
 *
 * Two choices, nothing automatic:
 * - Dismiss the card — the reply text is the prefilled, editable dismiss
 *   reason (the seed of the Merlin learning loop, owner ruling 7a3fa712).
 *   Goes through the same POST .../edit-proposals/{id}/dismiss the card uses.
 * - Keep it open — the CSR will Confirm it from the card (the only path that
 *   applies a change to Sage) or decide later.
 *
 * Nothing from the message body is recorded by the usage recorder: the
 * buttons carry a stable action name, the choice, and the ids only.
 */
import { useState } from 'react';

import { portalOrderService } from '../../services/portalOrders';
import type { PortalEmailEditProposal } from '../../types/portal';
import {
  DISMISS_REASON_MAX,
  dismissReasonFromReply,
} from '../../utils/editProposalReplySettle';
import { Modal } from '../common/Modal';
import styles from './EditProposalReplySettlePrompt.module.css';

export interface EditProposalReplySettlePromptProps {
  proposal: PortalEmailEditProposal;
  /** The CSR's own typed reply text — prefills the dismiss reason. */
  replyText: string;
  /** Fired after a successful dismiss so the page refetches. */
  onDismissed: () => void;
  /** Fired when the CSR keeps the card open (or closes the prompt). */
  onKeepOpen: () => void;
}

export default function EditProposalReplySettlePrompt({
  proposal,
  replyText,
  onDismissed,
  onKeepOpen,
}: EditProposalReplySettlePromptProps) {
  const [reason, setReason] = useState(() => dismissReasonFromReply(replyText));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orderLabel = proposal.sage_order_no ?? 'this order';

  const dismiss = async () => {
    setBusy(true);
    setError(null);
    try {
      await portalOrderService.dismissEditProposal(proposal.order_id, proposal.id, reason.trim());
      onDismissed();
    } catch (err) {
      console.error('Failed to dismiss the Merlin edit proposal after the reply:', err);
      setError('Could not dismiss the card. It is still open on the email and the order.');
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onKeepOpen} title={`Is the change on ${orderLabel} settled?`} size="sm">
      <div
        className={styles.body}
        data-testid="edit-proposal-reply-settle"
        data-target-proposal-id={proposal.id}
        data-target-order-id={proposal.order_id}
      >
        <p className={styles.lead}>
          You just answered this customer. The Merlin change card for order{' '}
          <strong>{orderLabel}</strong> is still open, so the order ships as it is in Sage
          unless the card is confirmed.
        </p>
        <div className={styles.summary} data-testid="edit-proposal-reply-settle-summary">
          {proposal.summary}
        </div>

        <label className={styles.label} htmlFor="edit-proposal-reply-settle-reason">
          Why is it settled? Saved as the card’s dismiss reason.
        </label>
        <textarea
          id="edit-proposal-reply-settle-reason"
          className={styles.reason}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, DISMISS_REASON_MAX))}
          maxLength={DISMISS_REASON_MAX}
          rows={4}
          disabled={busy}
          data-testid="edit-proposal-reply-settle-reason"
        />
        <div className={styles.counter}>
          {reason.length}/{DISMISS_REASON_MAX}
        </div>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.keepBtn}
            onClick={onKeepOpen}
            disabled={busy}
            data-testid="edit-proposal-reply-settle-keep"
            data-action="edit_proposal_settle_after_reply"
            data-record-value="choice"
            value="keep_open"
            data-outcome="close"
          >
            Keep it open
          </button>
          <button
            type="button"
            className={styles.dismissBtn}
            onClick={() => void dismiss()}
            disabled={busy}
            data-testid="edit-proposal-reply-settle-dismiss"
            data-action="edit_proposal_settle_after_reply"
            data-record-value="choice"
            value="dismiss"
            data-outcome="request"
          >
            {busy ? 'Dismissing…' : 'Dismiss the card'}
          </button>
        </div>
        <p className={styles.hint}>
          To apply the change to Sage, keep it open and use <strong>Confirm</strong> on the card.
        </p>
      </div>
    </Modal>
  );
}
